import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Check, Trash2, ClipboardList, Search, X, Pencil, Send, Eye, ArrowLeft, XCircle } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { money, outletNames, formatDate } from "../lib/format";
import { useOutlets } from "../lib/useOutlets";
import { PageIntro, PanelHead, Modal, SelectField, Field, Badge, Pagination } from "../components/UI";
import { usePagination } from "../lib/usePagination";
import { useAuth } from "../context/AuthContext";

const TABS = [
  { id: "opname", label: "Stock take (Main Warehouse)" },
  { id: "physical-count", label: "Outlet physical count" },
];

export default function OpnamePage() {
  const [tab, setTab] = useState("opname");
  const { user } = useAuth();
  const canCreate = ["admin", "warehouse"].includes(user?.role);

  return (
    <>
      <PageIntro
        eyebrow="Operations · reconciliation"
        title="Stock take"
        subtitle="Main Warehouse stock take adjusts system stock. Outlet counts are reference-only logs."
        testid="opname-title"
      />
      <section className="panel">
        <div className="tabs" role="tablist">
          {TABS.map((t) => (
            <button
              key={t.id}
              role="tab"
              aria-selected={t.id === tab}
              data-testid={`opname-tab-${t.id}`}
              className={`chip ${t.id === tab ? "active" : ""}`}
              onClick={() => setTab(t.id)}
            >
              {t.label}
            </button>
          ))}
        </div>
      </section>
      {tab === "opname" && <SystemOpnamePanel canCreate={canCreate} />}
      {tab === "physical-count" && <PhysicalCountPanel />}
    </>
  );
}

/* -------------------- System stock take (Main Warehouse) ------------------- */
const STATUS_META = {
  draft: { label: "Draft", tone: "amber" },
  submitted: { label: "Submitted", tone: "blue" },
  approved: { label: "Approved", tone: "green" },
  rejected: { label: "Rejected", tone: "danger" },
};
const EMPTY_FILTERS = { q: "", from: "", to: "", outlet: "all", status: "all" };

function matchesOpname(o, f) {
  const q = f.q.trim().toLowerCase();
  const outletName = (outletNames[o.outlet_code] || o.outlet_code || "").toLowerCase();
  const date = (o.created_at || "").slice(0, 10);
  if (q) {
    const hay = [o.opname_number, outletName, o.outlet_code, STATUS_META[o.status]?.label, o.status, o.counted_by, date, formatDate(o.created_at)]
      .filter(Boolean).join(" ").toLowerCase();
    if (!hay.includes(q)) return false;
  }
  if (f.from && date < f.from) return false;
  if (f.to && date > f.to) return false;
  if (f.outlet !== "all" && o.outlet_code !== f.outlet) return false;
  if (f.status !== "all" && o.status !== f.status) return false;
  return true;
}

function SystemOpnamePanel({ canCreate }) {
  const { user } = useAuth();
  const outletsList = useOutlets();
  const [list, setList] = useState([]);
  const [items, setItems] = useState([]);
  const [modal, setModal] = useState(null); // null | { mode: "new" } | { mode: "edit", opname }
  const [reviewId, setReviewId] = useState(null);
  const [filters, setFilters] = useState(EMPTY_FILTERS);

  const canApprove = ["admin", "finance"].includes(user?.role);

  const load = () => api.get("/opnames").then((r) => setList(r.data));
  const loadItems = () => api.get("/items").then((r) => setItems(r.data));
  useEffect(() => {
    load();
    loadItems();
  }, []);

  const filtered = useMemo(() => list.filter((o) => matchesOpname(o, filters)), [list, filters]);
  const pager = usePagination(filtered, 100, JSON.stringify(filters));
  const isFiltered = JSON.stringify(filters) !== JSON.stringify(EMPTY_FILTERS);

  const act = async (fn, okMsg) => {
    try {
      await fn();
      toast.success(okMsg);
      load();
      loadItems();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };
  const submit = (o) => {
    if (!window.confirm(`Submit ${o.opname_number} for review? It can no longer be edited until reviewed.`)) return;
    act(() => api.post(`/opnames/${o.id}/submit`), "Stock take submitted for review");
  };

  if (reviewId) {
    return (
      <OpnameReview
        id={reviewId}
        canApprove={canApprove}
        onBack={() => { setReviewId(null); load(); loadItems(); }}
      />
    );
  }

  return (
    <>
      <section className="panel">
        <div className="toolbar" style={{ flexWrap: "wrap", alignItems: "flex-end" }}>
          {canCreate && (
            <button data-testid="opname-primary-action" className="primary-button" onClick={() => setModal({ mode: "new" })}>
              <Plus size={17} /> Start stock take
            </button>
          )}
          <div className="search-box" style={{ minWidth: 240 }}>
            <Search size={15} />
            <input
              data-testid="opname-search"
              placeholder="Search number, outlet, status, user, date..."
              value={filters.q}
              onChange={(e) => setFilters({ ...filters, q: e.target.value })}
            />
          </div>
          <label className="field small"><span>From</span>
            <input data-testid="opname-filter-from" type="date" value={filters.from} onChange={(e) => setFilters({ ...filters, from: e.target.value })} />
          </label>
          <label className="field small"><span>To</span>
            <input data-testid="opname-filter-to" type="date" value={filters.to} onChange={(e) => setFilters({ ...filters, to: e.target.value })} />
          </label>
          <label className="field small"><span>Outlet</span>
            <select data-testid="opname-filter-outlet" value={filters.outlet} onChange={(e) => setFilters({ ...filters, outlet: e.target.value })}>
              <option value="all">All</option>
              {outletsList.map((o) => <option key={o.code} value={o.code}>{o.name}</option>)}
            </select>
          </label>
          <label className="field small"><span>Status</span>
            <select data-testid="opname-filter-status" value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}>
              <option value="all">All</option>
              {Object.entries(STATUS_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </label>
          {isFiltered && (
            <button data-testid="opname-filter-clear" className="secondary-button" onClick={() => setFilters(EMPTY_FILTERS)}>
              <X size={14} /> Clear
            </button>
          )}
        </div>
        <PanelHead
          title="Stock take history"
          detail={`Showing ${filtered.length} of ${list.length} sessions · Main Warehouse only`}
        />
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>No.</th>
                <th>Outlet</th>
                <th>Lines</th>
                <th>Variance value</th>
                <th>Status</th>
                <th>Created by</th>
                <th>Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={8} style={{ textAlign: "center", padding: 40 }} data-testid="opname-empty">
                  {list.length === 0 ? "No stock takes yet." : "No stock takes match your search / filters."}
                </td></tr>
              )}
              {pager.pageItems.map((o) => {
                const meta = STATUS_META[o.status] || { label: o.status, tone: "neutral" };
                const editable = o.status === "draft" || o.status === "rejected";
                return (
                  <tr key={o.id} data-testid={`opname-row-${o.id}`}>
                    <td><strong>{o.opname_number}</strong></td>
                    <td>{outletNames[o.outlet_code] || o.outlet_code}</td>
                    <td>{o.items.length}</td>
                    <td className={o.total_variance_value < 0 ? "danger-text" : o.total_variance_value > 0 ? "success-text" : ""}>
                      <strong>{money(o.total_variance_value)}</strong>
                    </td>
                    <td><Badge tone={meta.tone}>{meta.label}</Badge></td>
                    <td><small>@{o.counted_by}</small></td>
                    <td><small>{formatDate(o.created_at)}</small></td>
                    <td>
                      <div className="badge-cluster">
                        {editable && canCreate && (
                          <button data-testid={`opname-edit-${o.id}`} className="small-button" onClick={() => setModal({ mode: "edit", opname: o })}>
                            <Pencil size={12} /> Edit
                          </button>
                        )}
                        {editable && canCreate && (
                          <button data-testid={`opname-submit-${o.id}`} className="small-button" onClick={() => submit(o)}>
                            <Send size={12} /> Submit
                          </button>
                        )}
                        <button
                          data-testid={`opname-review-${o.id}`}
                          className={`small-button ${o.status === "submitted" && canApprove ? "success" : ""}`}
                          onClick={() => setReviewId(o.id)}
                        >
                          <Eye size={12} /> {o.status === "submitted" && canApprove ? "Review" : "View"}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <Pagination page={pager.page} pageCount={pager.pageCount} total={pager.total} pageSize={pager.pageSize} onChange={pager.setPage} testid="opname-pagination" />
      </section>

      {modal && (
        <OpnameFormModal
          mode={modal.mode}
          opname={modal.opname}
          items={items}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); load(); }}
        />
      )}
    </>
  );
}

/* ------------------------- Stock take form (new / edit) ------------------------ */
function OpnameFormModal({ mode, opname, items, onClose, onSaved }) {
  const [notes, setNotes] = useState(opname?.notes || "");
  const [physical, setPhysical] = useState(() => {
    if (!opname) return {};
    return Object.fromEntries(opname.items.map((l) => [l.item_id, String(l.physical_qty)]));
  });
  const [q, setQ] = useState("");
  const [saving, setSaving] = useState(false);

  const visible = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return items;
    return items.filter((i) => [i.sku, i.name, i.category].some((v) => (v || "").toLowerCase().includes(s)));
  }, [items, q]);

  const submit = async (e) => {
    e.preventDefault();
    const lines = items.map((i) => ({
      item_id: i.id,
      name: i.name,
      physical_qty: physical[i.id] !== undefined && physical[i.id] !== "" ? Number(physical[i.id]) : i.stock,
    }));
    if (lines.length === 0) return toast.error("No items to opname");
    setSaving(true);
    try {
      if (mode === "edit") {
        await api.put(`/opnames/${opname.id}`, { outlet_code: "main_wh", notes, items: lines });
        toast.success("Stock take updated");
      } else {
        await api.post("/opnames", { outlet_code: "main_wh", notes, items: lines });
        toast.success("Stock take saved (draft) — submit it for review when ready");
      }
      onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title={mode === "edit" ? `Edit ${opname.opname_number}` : "Stock take — Main Warehouse"} onClose={onClose}>
      <form onSubmit={submit}>
        <div className="form-grid">
          <label className="field">
            <span>Outlet</span>
            <input data-testid="opname-outlet-select" value="Main Warehouse" disabled />
            <small className="form-hint">
              System stock take adjusts the Main Warehouse balance after approval. For outlets, use the
              "Outlet physical count" tab (reference log only).
            </small>
          </label>
          <label className="field">
            <span>Notes</span>
            <input data-testid="opname-notes-input" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Monthly stock take..." />
          </label>
        </div>
        {mode === "edit" && opname.status === "rejected" && opname.rejection_notes && (
          <div className="rejection-note" data-testid="opname-edit-rejection-note">
            <strong>Rejected by @{opname.rejected_by}:</strong> {opname.rejection_notes}
          </div>
        )}
        <div className="toolbar" style={{ padding: "14px 0 6px", borderBottom: 0, alignItems: "center" }}>
          <div className="search-box">
            <Search size={15} />
            <input
              data-testid="opname-item-search"
              placeholder="Find item by code, name, or category..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
          <small data-testid="opname-item-counter" style={{ marginLeft: "auto", color: "var(--muted)" }}>
            Showing {visible.length} of {items.length} items
          </small>
        </div>
        <div className="opname-list" data-testid="opname-item-list">
          <div className="opname-head">
            <span>Item</span>
            <span>System</span>
            <span>Physical</span>
            <span>Variance</span>
          </div>
          {items.length === 0 && <p className="empty-hint">No items.</p>}
          {items.length > 0 && visible.length === 0 && <p className="empty-hint" style={{ padding: 16 }}>No items match "{q}".</p>}
          {visible.map((i) => {
            const phys = physical[i.id] === undefined || physical[i.id] === "" ? i.stock : Number(physical[i.id]);
            const variance = phys - i.stock;
            return (
              <div className="opname-row" key={i.id}>
                <div>
                  <strong>{i.name}</strong>
                  <small>{i.sku} · {i.category || "-"} · {i.unit}</small>
                </div>
                <div><strong>{i.stock}</strong></div>
                <input
                  data-testid={`opname-physical-${i.id}`}
                  type="number"
                  step="any"
                  value={physical[i.id] ?? ""}
                  placeholder={String(i.stock)}
                  onChange={(e) => setPhysical({ ...physical, [i.id]: e.target.value })}
                />
                <div className={variance < 0 ? "danger-text" : variance > 0 ? "success-text" : ""}>
                  <strong>{variance > 0 ? `+${variance}` : variance}</strong>
                  <small>{money(variance * i.cost)}</small>
                </div>
              </div>
            );
          })}
        </div>
        <div className="form-actions">
          <button type="button" className="secondary-button" onClick={onClose}>Cancel</button>
          <button data-testid="opname-save-button" className="primary-button" type="submit" disabled={saving}>
            <Check size={16} /> {saving ? "Saving..." : mode === "edit" ? "Save changes" : "Save stock take"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

/* ------------------------- Review / detail screen ------------------------- */
function OpnameReview({ id, canApprove, onBack }) {
  const [op, setOp] = useState(null);
  const [q, setQ] = useState("");
  const [onlyVariance, setOnlyVariance] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/opnames/${id}`).then((r) => setOp(r.data)).catch(() => { toast.error("Stock take not found"); onBack(); });
  useEffect(() => { load(); }, [id]); // eslint-disable-line

  const visible = useMemo(() => {
    if (!op) return [];
    const s = q.trim().toLowerCase();
    return op.items.filter((l) => {
      if (onlyVariance && Number(l.variance) === 0) return false;
      if (!s) return true;
      return [l.sku, l.name, l.category].some((v) => (v || "").toLowerCase().includes(s));
    });
  }, [op, q, onlyVariance]);

  if (!op) return <div className="loading-state">Loading...</div>;

  const meta = STATUS_META[op.status] || { label: op.status, tone: "neutral" };
  const withVariance = op.items.filter((l) => Number(l.variance) !== 0);
  const plus = op.items.reduce((s, l) => s + (l.variance_value > 0 ? l.variance_value : 0), 0);
  const minus = op.items.reduce((s, l) => s + (l.variance_value < 0 ? l.variance_value : 0), 0);
  const reviewable = op.status === "submitted" && canApprove;

  const approve = async () => {
    if (!window.confirm(`Approve ${op.opname_number}? Main Warehouse stock will be adjusted to the physical quantities.`)) return;
    setBusy(true);
    try {
      await api.post(`/opnames/${op.id}/approve`);
      toast.success("Stock take approved & stock adjusted");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };
  const reject = async () => {
    if (!reason.trim()) return toast.error("Rejection reason is required");
    setBusy(true);
    try {
      await api.post(`/opnames/${op.id}/reject`, { notes: reason.trim() });
      toast.success("Stock take rejected — returned to warehouse for correction");
      setRejecting(false);
      setReason("");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="panel" data-testid="opname-review">
      <div className="toolbar" style={{ alignItems: "center" }}>
        <button data-testid="opname-review-back" className="secondary-button" onClick={onBack}>
          <ArrowLeft size={14} /> Back
        </button>
        <div style={{ marginLeft: 4 }}>
          <strong style={{ fontSize: 16 }}>{op.opname_number}</strong>{" "}
          <Badge tone={meta.tone}>{meta.label}</Badge>
          <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 4 }} data-testid="opname-review-meta">
            {outletNames[op.outlet_code] || op.outlet_code} · counted by @{op.counted_by} · {formatDate(op.created_at)}
            {op.submitted_by && <> · submitted by @{op.submitted_by} {formatDate(op.submitted_at)}</>}
            {op.approved_by && <> · <span className="success-text"><strong>approved by @{op.approved_by} {formatDate(op.approved_at)}</strong></span></>}
            {op.status === "rejected" && op.rejected_by && <> · <span className="danger-text"><strong>rejected by @{op.rejected_by} {formatDate(op.rejected_at)}</strong></span></>}
          </div>
          {op.notes && <div style={{ fontSize: 12, marginTop: 4 }}>Notes: {op.notes}</div>}
        </div>
      </div>

      {op.rejection_notes && (
        <div className="rejection-note" data-testid="opname-rejection-notes" style={{ margin: "14px 20px 0" }}>
          <strong>Rejection notes{op.status !== "rejected" ? " (previous review)" : ""}:</strong> {op.rejection_notes}
        </div>
      )}

      <div className="report-summary" style={{ padding: "16px 20px 0" }} data-testid="opname-review-summary">
        <div className="summary-tile"><span>Items counted</span><strong data-testid="review-total-items">{op.items.length}</strong></div>
        <div className="summary-tile"><span>Items with variance</span><strong className="amber" data-testid="review-variance-items">{withVariance.length}</strong></div>
        <div className="summary-tile"><span>Variance (+)</span><strong className="green" data-testid="review-variance-plus">{money(plus)}</strong></div>
        <div className="summary-tile"><span>Variance (−)</span><strong className="danger" data-testid="review-variance-minus">{money(minus)}</strong></div>
        <div className="summary-tile"><span>Net variance</span>
          <strong className={op.total_variance_value < 0 ? "danger" : op.total_variance_value > 0 ? "green" : ""} data-testid="review-variance-net">{money(op.total_variance_value)}</strong>
        </div>
      </div>

      <div className="toolbar" style={{ borderBottom: 0, alignItems: "center" }}>
        <div className="search-box">
          <Search size={15} />
          <input data-testid="opname-review-search" placeholder="Search item code, name, category..." value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <label className="checkbox-inline" style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 12, cursor: "pointer" }}>
          <input data-testid="opname-review-only-variance" type="checkbox" checked={onlyVariance} onChange={(e) => setOnlyVariance(e.target.checked)} />
          Show only items with variance
        </label>
        <small style={{ marginLeft: "auto", color: "var(--muted)" }} data-testid="opname-review-counter">Showing {visible.length} of {op.items.length} items</small>
      </div>

      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Code</th><th>Item</th><th>Category</th><th>Unit</th>
              <th style={{ textAlign: "right" }}>System</th><th style={{ textAlign: "right" }}>Physical</th><th style={{ textAlign: "right" }}>Variance</th>
              <th style={{ textAlign: "right" }}>Unit cost</th><th style={{ textAlign: "right" }}>Variance value</th>
            </tr>
          </thead>
          <tbody>
            {visible.length === 0 && <tr><td colSpan={9} style={{ textAlign: "center", padding: 40 }}>No items match.</td></tr>}
            {visible.map((l) => {
              const v = Number(l.variance);
              const cls = v < 0 ? "row-neg" : v > 0 ? "row-pos" : "";
              return (
                <tr key={l.item_id} className={cls} data-testid={`review-row-${l.item_id}`}>
                  <td><Badge tone="neutral">{l.sku || "-"}</Badge></td>
                  <td><strong>{l.name}</strong></td>
                  <td>{l.category || "-"}</td>
                  <td>{l.unit}</td>
                  <td style={{ textAlign: "right" }}>{l.system_qty}</td>
                  <td style={{ textAlign: "right" }}><strong>{l.physical_qty}</strong></td>
                  <td style={{ textAlign: "right" }}><strong>{v > 0 ? `+${v}` : v}</strong></td>
                  <td style={{ textAlign: "right" }}>{money(l.cost)}</td>
                  <td style={{ textAlign: "right" }}><strong>{money(l.variance_value)}</strong></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="form-actions" style={{ padding: "16px 20px", flexWrap: "wrap" }}>
        {rejecting && (
          <textarea
            data-testid="opname-reject-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Reason for rejection (required)..."
            rows={2}
            style={{ flex: "1 1 100%", font: "13px 'DM Sans'", padding: 10, border: "1px solid var(--line)", borderRadius: 6 }}
          />
        )}
        <button data-testid="opname-review-back-bottom" type="button" className="secondary-button" onClick={onBack}>
          <ArrowLeft size={14} /> Back
        </button>
        {reviewable && !rejecting && (
          <button data-testid="opname-review-reject" type="button" className="secondary-button danger-text" onClick={() => setRejecting(true)} disabled={busy}>
            <XCircle size={14} /> Reject
          </button>
        )}
        {reviewable && rejecting && (
          <>
            <button type="button" className="secondary-button" onClick={() => { setRejecting(false); setReason(""); }} disabled={busy}>Cancel</button>
            <button data-testid="opname-review-reject-confirm" type="button" className="primary-button" style={{ background: "var(--danger)" }} onClick={reject} disabled={busy}>
              <XCircle size={14} /> Confirm reject
            </button>
          </>
        )}
        {reviewable && !rejecting && (
          <button data-testid="opname-review-approve" type="button" className="primary-button" onClick={approve} disabled={busy}>
            <Check size={16} /> Approve &amp; adjust stock
          </button>
        )}
      </div>
    </section>
  );
}

/* --------------------- Outlet physical count (log only) -------------------- */
function PhysicalCountPanel() {
  const { user } = useAuth();
  const outletsList = useOutlets();
  const outletOptions = outletsList.filter((o) => o.code !== "main_wh" && o.type !== "warehouse");
  const [rows, setRows] = useState([]);
  const [items, setItems] = useState([]);
  const [outletFilter, setOutletFilter] = useState("all");
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({ outlet_code: "", item_id: "", qty: "", counted_at: "", note: "" });
  const [saving, setSaving] = useState(false);

  const load = () =>
    api
      .get("/physical-counts", { params: { outlet: outletFilter !== "all" ? outletFilter : undefined } })
      .then((r) => setRows(r.data));
  useEffect(() => {
    load();
  }, [outletFilter]); // eslint-disable-line
  useEffect(() => {
    api.get("/items").then((r) => setItems(r.data));
  }, []);

  const openNew = () => {
    setForm({ outlet_code: outletOptions[0]?.code || "", item_id: "", qty: "", counted_at: "", note: "" });
    setModal("new");
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.outlet_code) return toast.error("Select an outlet");
    if (!form.item_id) return toast.error("Select an item");
    if (form.qty === "" || Number(form.qty) < 0) return toast.error("Enter the counted quantity");
    setSaving(true);
    try {
      await api.post("/physical-counts", {
        outlet_code: form.outlet_code,
        item_id: form.item_id,
        qty: Number(form.qty),
        counted_at: form.counted_at || null,
        note: form.note,
      });
      toast.success("Physical count logged (reference only — outlet balance stays 0)");
      setModal(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (r) => {
    if (!window.confirm("Delete this physical count log entry?")) return;
    try {
      await api.delete(`/physical-counts/${r.id}`);
      toast.success("Deleted");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <section className="panel">
      <PanelHead
        title="Outlet physical count log"
        detail={`${rows.length} record(s) · reference only — never changes system balances (outlets are always 0)`}
      />
      <div className="toolbar">
        <button data-testid="physical-count-add" className="primary-button" onClick={openNew}>
          <ClipboardList size={17} /> Log physical count
        </button>
        <select
          data-testid="physical-count-outlet-filter"
          value={outletFilter}
          onChange={(e) => setOutletFilter(e.target.value)}
          style={{ marginLeft: "auto" }}
        >
          <option value="all">All outlets</option>
          {outletOptions.map((o) => (
            <option key={o.code} value={o.code}>{o.name}</option>
          ))}
        </select>
      </div>
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Date</th><th>Outlet</th><th>Item</th><th>Qty observed</th><th>Counted by</th><th>Note</th><th style={{ textAlign: "right" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={7} style={{ textAlign: "center", padding: 40 }}>No physical counts logged yet.</td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.id} data-testid={`physical-count-row-${r.id}`}>
                <td><small>{formatDate(r.counted_at)}</small></td>
                <td><strong>{outletNames[r.outlet_code] || r.outlet_code}</strong></td>
                <td>{r.item_name} <small>{r.unit}</small></td>
                <td><strong>{r.qty}</strong></td>
                <td><small>@{r.counted_by}</small></td>
                <td><small>{r.note || "-"}</small></td>
                <td style={{ textAlign: "right" }}>
                  {user?.role === "admin" && (
                    <button data-testid={`physical-count-delete-${r.id}`} className="small-button" onClick={() => remove(r)}>
                      <Trash2 size={12} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modal === "new" && (
        <Modal title="Log outlet physical count" onClose={() => setModal(null)}>
          <form className="form-grid" onSubmit={save}>
            <SelectField
              label="Outlet"
              testid="physical-count-outlet"
              value={form.outlet_code}
              onChange={(v) => setForm({ ...form, outlet_code: v })}
              options={outletOptions.map((o) => ({ value: o.code, label: o.name }))}
            />
            <SelectField
              label="Item"
              testid="physical-count-item"
              value={form.item_id}
              onChange={(v) => setForm({ ...form, item_id: v })}
              options={[{ value: "", label: "— Select item —" }, ...items.map((i) => ({ value: i.id, label: `${i.name} (${i.unit})` }))]}
            />
            <Field label="Qty observed" testid="physical-count-qty" type="number" value={form.qty} onChange={(v) => setForm({ ...form, qty: v })} />
            <Field label="Count date (optional)" testid="physical-count-date" type="date" value={form.counted_at} onChange={(v) => setForm({ ...form, counted_at: v })} />
            <label className="field" style={{ gridColumn: "1/-1" }}>
              <span>Note (optional)</span>
              <input data-testid="physical-count-note" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="End-of-period check, pre-order count..." />
            </label>
            <div className="form-actions">
              <button type="button" className="secondary-button" onClick={() => setModal(null)}>Cancel</button>
              <button type="submit" data-testid="physical-count-save" className="primary-button" disabled={saving}>
                <Check size={16} /> {saving ? "Saving..." : "Save log"}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </section>
  );
}
